import { Sidebar } from "./Sidebar";
import { Header } from "./Header";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";

interface LayoutProps {
  children: React.ReactNode;
}

/**
 * Convierte un color Hexadecimal (#RRGGBB) a formato HSL (H S% L%)
 * compatible con las variables de CSS de Shadcn UI / Tailwind.
 */
function hexToHsl(hex: string): string {
  // Limpiar el hash
  hex = hex.replace(/^#/, '');
  
  // Parsear los componentes
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function Layout({ children }: LayoutProps) {
  const { company } = useAuth();

  // Generar variables HSL dinámicas
  const primaryHsl = company?.primary_color ? hexToHsl(company.primary_color) : null;
  const secondaryHsl = company?.secondary_color ? hexToHsl(company.secondary_color) : null;

  return (
    <div 
      className="flex h-screen bg-slate-50"
      style={{
        // @ts-ignore
        '--primary-company': company?.primary_color || '#3b82f6',
        '--secondary-company': company?.secondary_color || '#64748b',
        ...(primaryHsl ? { '--primary': primaryHsl } : {}),
        ...(secondaryHsl ? { '--secondary': secondaryHsl } : {}),
      } as React.CSSProperties}
    >
      <Sidebar />

      <div className="flex flex-1 flex-col overflow-hidden">
        <Header />

        <main
          className={cn(
            "flex-1 overflow-y-auto transition-all duration-300 ease-in-out",
            "pt-28 pb-28 md:pb-32",
            "px-4 md:px-8 lg:px-12"
          )}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
